/**
 * Service for handling inline citation detection and insertion
 */

export interface InlineCitationTrigger {
  detected: boolean;
  searchText: string;
  range?: Word.Range;
}

/**
 * Checks if the user has typed '@' followed by text to trigger inline citation
 */
export async function detectInlineCitationTrigger(): Promise<InlineCitationTrigger> {
  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      selection.load("text");
      
      // Get the paragraph containing the selection to check for '@'
      const paragraph = selection.paragraphs.getFirst();
      paragraph.load("text");
      
      await context.sync();
      
      const paragraphText = paragraph.text;
      const selectionText = selection.text;
      
      // Look for '@' pattern in the current paragraph
      // Match '@' followed by optional text, up to the cursor position
      const atIndex = paragraphText.lastIndexOf("@");
      
      if (atIndex !== -1) {
        // Check if '@' is reasonably close to the end (within last 50 chars)
        const distanceFromEnd = paragraphText.length - atIndex;
        if (distanceFromEnd <= 50) {
          // Extract the search text after '@'
          const searchText = paragraphText.substring(atIndex + 1).trim();
          
          // Get the range containing '@searchText'
          const searchResults = paragraph.search("@" + searchText, { matchCase: false, matchWholeWord: false });
          searchResults.load("items");
          await context.sync();
          
          if (searchResults.items.length > 0) {
            const range = searchResults.items[searchResults.items.length - 1];
            return {
              detected: true,
              searchText: searchText,
              range: range,
            };
          }
        }
      }
      
      return { detected: false, searchText: "" };
    } catch (error) {
      console.error("Error detecting inline citation trigger:", error);
      return { detected: false, searchText: "" };
    }
  });
}

/**
 * Removes the '@searchText' trigger and inserts the citation at that location
 */
export async function replaceInlineTriggerWithCitation(
  triggerRange: Word.Range,
  citationText: string
): Promise<void> {
  return Word.run(async (context) => {
    try {
      // Delete the '@searchText' 
      triggerRange.delete();
      
      // Insert the citation at that position
      const insertedRange = triggerRange.insertText(citationText, Word.InsertLocation.start);
      
      await context.sync();
    } catch (error) {
      console.error("Error replacing inline trigger:", error);
      throw error;
    }
  });
}

/**
 * Alternative approach: Insert citation at current cursor position with content control tracking
 */
export async function insertCitationAtCursor(citationText: string, paperId: string): Promise<void> {
  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      
      // Check if we can find and remove '@' pattern before cursor
      const paragraph = selection.paragraphs.getFirst();
      paragraph.load("text");
      await context.sync();
      
      const text = paragraph.text;
      const atIndex = text.lastIndexOf("@");
      
      let citationRange: Word.Range;
      
      if (atIndex !== -1 && (text.length - atIndex) <= 50) {
        // Remove the '@...' pattern
        const searchPattern = text.substring(atIndex);
        const searchResults = paragraph.search(searchPattern, { matchCase: false });
        searchResults.load("items");
        await context.sync();
        
        if (searchResults.items.length > 0) {
          const lastMatch = searchResults.items[searchResults.items.length - 1];
          lastMatch.delete();
          citationRange = lastMatch.insertText(citationText, Word.InsertLocation.start);
        } else {
          // Fallback: insert at cursor
          citationRange = selection.insertText(citationText, Word.InsertLocation.replace);
        }
      } else {
        // Just insert at cursor
        citationRange = selection.insertText(citationText, Word.InsertLocation.replace);
      }
      
      // Wrap in content control for tracking
      const contentControl = citationRange.insertContentControl();
      contentControl.tag = "scholarsync-citation";
      contentControl.title = paperId;
      contentControl.appearance = "BoundingBox";
      contentControl.color = "#e0e0e0";
      
      await context.sync();
      
      console.log(`✅ Inline citation inserted with tracking: ${citationText} [${paperId}]`);
    } catch (error) {
      console.error("Error inserting citation at cursor:", error);
      throw error;
    }
  });
}
